import { PictureOutlined } from '@ant-design/icons';
import { Tag, Tooltip } from 'antd';
import type { CSSProperties, ReactNode } from 'react';

import {
  ShowcaseProjectCategory,
  ShowcaseProjectStatus,
  showcaseProjectCategoryNames,
  showcaseProjectStatusNames,
} from '../../api/showcaseProjects';
import type {
  ShowcaseProject,
  ShowcaseProjectCategoryValue,
  ShowcaseProjectStatusValue,
} from '../../api/showcaseProjects';
import { formatDate } from '../projects/projectDisplay';
import { BrandColors } from '../../theme/brand';

/**
 * Phần hiển thị dùng chung của trang "Demo dự án": bảng, thẻ xem trước và dải số liệu.
 *
 * Màu và cách viết bám theo thẻ trên website, để người quản trị nhìn trang này là hình dung được
 * trang công khai.
 */

/** Số công nghệ thẻ trên website hiện ra trước khi gộp thành "+N". */
export const visibleTechnologyCount = 4;

/** Số thành tích thẻ trên website hiện ra; cửa sổ chi tiết mới hiện hết. */
export const visibleAchievementCount = 2;

/** Lời hỏi lại trước khi xóa — chung cho bảng và thẻ để hai chỗ không nói hai kiểu. */
export const deleteConfirmText = {
  title: 'Xóa dự án tiêu biểu này?',
  description: 'Dự án sẽ biến mất khỏi website. Muốn tạm gỡ thì tắt công tắc Hiện trên web.',
} as const;

/** Màu chủ đạo của từng tình trạng — dùng cho nhãn, thanh tiến độ và dải số liệu. */
export const statusColors: Readonly<Record<ShowcaseProjectStatusValue, string>> = {
  [ShowcaseProjectStatus.Ongoing]: BrandColors.blue,
  [ShowcaseProjectStatus.Completed]: BrandColors.green,
};

/** Thẻ màu cho lĩnh vực. */
export function categoryTag(category: ShowcaseProjectCategoryValue): ReactNode {
  return (
    <Tag
      bordered={false}
      color={category === ShowcaseProjectCategory.ComputerVision ? 'geekblue' : 'purple'}
      style={{ marginInlineEnd: 0 }}
    >
      {showcaseProjectCategoryNames[category]}
    </Tag>
  );
}

/** Thẻ màu cho tình trạng, có chấm màu như website. */
export function statusTag(status: ShowcaseProjectStatusValue): ReactNode {
  return (
    <Tag
      bordered={false}
      color={status === ShowcaseProjectStatus.Completed ? 'success' : 'processing'}
      style={{ marginInlineEnd: 0 }}
    >
      <span
        style={{
          display: 'inline-block',
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: statusColors[status],
          marginInlineEnd: 6,
          verticalAlign: 'middle',
        }}
      />
      {showcaseProjectStatusNames[status]}
    </Tag>
  );
}

/** Các thẻ công nghệ: mấy mục đầu, phần còn lại gộp thành "+N" kèm chú thích. */
export function technologyTags(technologies: readonly string[]): ReactNode {
  const shown = technologies.slice(0, visibleTechnologyCount);
  const hidden = technologies.slice(visibleTechnologyCount);

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {shown.map((technology) => (
        <Tag key={technology} color="blue" style={{ marginInlineEnd: 0 }}>
          {technology}
        </Tag>
      ))}
      {hidden.length > 0 ? (
        <Tooltip title={hidden.join(', ')}>
          <Tag style={{ marginInlineEnd: 0, cursor: 'default' }}>+{hidden.length.toString()}</Tag>
        </Tooltip>
      ) : null}
    </div>
  );
}

/** Khoảng thời gian dạng "01/04/2024 – 15/08/2024", hoặc "– nay" khi chưa kết thúc. */
export function formatPeriod(project: ShowcaseProject): string {
  const end = project.endDate === null ? 'nay' : formatDate(project.endDate);

  return `${formatDate(project.startDate)} – ${end}`;
}

/**
 * Ảnh minh họa, hoặc ô giữ chỗ khi dự án chưa có ảnh.
 *
 * Khung giữ đúng tỉ lệ 16:9 dù ảnh gốc có tỉ lệ gì, để các dòng và các thẻ thẳng hàng với nhau.
 */
export function ShowcaseImage(props: {
  readonly url: string | null;
  readonly alt: string;
  readonly style?: CSSProperties;
  readonly isDimmed?: boolean;
}): ReactNode {
  const frame: CSSProperties = {
    aspectRatio: '16 / 9',
    borderRadius: 6,
    overflow: 'hidden',
    background: BrandColors.surfaceSubtle,
    flexShrink: 0,
    ...props.style,
  };

  if (props.url === null) {
    return (
      <div
        style={{
          ...frame,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: BrandColors.inkMuted,
          fontSize: 22,
        }}
      >
        <PictureOutlined />
      </div>
    );
  }

  return (
    <div style={frame}>
      <img
        src={props.url}
        alt={props.alt}
        loading="lazy"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          // Dự án đang ẩn khỏi website: làm nhạt ảnh để nhìn lướt là nhận ra.
          ...(props.isDimmed === true ? { filter: 'grayscale(0.9) opacity(0.6)' } : {}),
        }}
      />
    </div>
  );
}
